"use client";

import { useCallback, useMemo, useState } from "react";

import { parseActiveScanIndex } from "./chart-scan";

type T_RechartsScanState = {
  activeTooltipIndex?: number | string | null | undefined;
  isTooltipActive?: boolean;
};

export type T_ChartScanHandlers = {
  onMouseLeave: () => void;
  onMouseMove: (state: T_RechartsScanState) => void;
  onTouchEnd: () => void;
  onTouchMove: (state: T_RechartsScanState) => void;
  onTouchStart: (state: T_RechartsScanState) => void;
};

/**
 * Index-keyed scan state for recharts plots per the chart interaction
 * policy: pointer hover/drag maps to the nearest data index
 * (activeTooltipIndex), the legend swaps to that point's values in place,
 * and leave/release returns to latest (scanIndex null). No tooltip overlay
 * ever renders on the plot.
 *
 * `enabled=false` (synthetic/fabricated fallback data) pins the readout to
 * latest — scanning fabricated data is forbidden.
 */
export function useChartScan(
  pointCount: number,
  enabled: boolean,
): { handlers: T_ChartScanHandlers; scanIndex: number | null } {
  const [scanIndex, setScanIndex] = useState<number | null>(null);

  const readState = useCallback(
    (state: T_RechartsScanState) => {
      if (!enabled) return;
      setScanIndex(parseActiveScanIndex(state.activeTooltipIndex, pointCount));
    },
    [enabled, pointCount],
  );

  const clear = useCallback(() => {
    setScanIndex(null);
  }, []);

  const handlers = useMemo<T_ChartScanHandlers>(
    () => ({
      onMouseLeave: clear,
      onMouseMove: readState,
      onTouchEnd: clear,
      onTouchMove: readState,
      onTouchStart: readState,
    }),
    [clear, readState],
  );

  return { handlers, scanIndex: enabled ? scanIndex : null };
}
